import { PRESCRIPTION_INFO } from '../../../constants/const'
import { localStorageWrapper } from '../../../helpers/localStorage'
import { BasicResult, CheckBox } from '../../../types/models/Presc'

// 処方箋存在領域はselected_optionがImageArea[]で他フィールドと形が異なるため表示グルーピング対象外
// (もともと画面表示用の項目ではなく分割検出用の内部データ)。
export type BasicResultFieldKey = Exclude<keyof BasicResult, '処方箋存在領域'>

export type FieldGroup = { heading: string; keys: BasicResultFieldKey[] }

/**
 * basic_result の表示グルーピング。全フィールドを含める(ほか依頼のみ末尾に隔離)。
 *
 * 並びは紙面の読み順。紙は上から 公費負担者番号/保険者番号 → 公費受給者番号/被保険者証記号番号
 * → 氏名・生年月日・性別/医療機関 → 区分/診療科・医師 → 交付年月日 の順で、
 * 保険の帯が最上段にある。グループの順序は「そのブロックが紙面で始まる高さ」で決め、
 * グループの中は紙の順にする(1行ずつ厳密になぞるとグループが消えて走査性が落ちる)。
 */
const INSURANCE_GROUP: FieldGroup = {
  heading: '保険',
  keys: [
    'id_27_1st_kouhi_futansha_num',
    'id_27_1st_kouhi_jukyuusha_num',
    'id_22_insurance_patient_num',
    'id_23_insurance_card_kigou',
    'id_23_insurance_card_bangou',
    'id_23_insurance_card_edaban',
    'id_23_insurance_type',
  ],
}

const PATIENT_GROUP: FieldGroup = {
  heading: '患者情報',
  keys: [
    'id_11_patient_name_kanji',
    'id_11_patient_name_kana',
    'id_13_patient_birthday',
    'id_12_patient_sex',
  ],
}

/**
 * コード3種(都道府県/点数表/医療機関コード)は**出さない**(松田 2026-08-29)。
 * 薬剤師が紙と突き合わせる欄ではなく、読んだ結果として付いてくる識別子。
 * 値は残り JAHIS の医療機関レコードには載る(画面から消えるのは表示だけ)。
 */
const HOSPITAL_GROUP: FieldGroup = {
  heading: '医療機関',
  keys: [
    'id_1_hospital_name',
    'id_4_hospital_department_name',
    'id_5_doctor_name_kanji',
    'id_5_doctor_name_kana',
    'id_51_prescription_date',
  ],
}

const REQUEST_GROUP: FieldGroup = {
  heading: 'ほか依頼',
  keys: ['複数医療機関分割', '複数診療科分割', '複数患者分割'],
}

/**
 * 版面。1 要素 = 1 段で、段の中に並ぶグループを横に置く。
 *
 * 紙は 保険が全幅の最上段、その下が 左=患者 / 右=医療機関 なので、面の組み方も同じにする
 * (04-apps/mamoru.md「項目の並び」)。1 列に積むと紙と違う形になり、目が置き場所を覚え直す。
 */
export const BASIC_RESULT_ROWS: FieldGroup[][] = [
  [INSURANCE_GROUP],
  [PATIENT_GROUP, HOSPITAL_GROUP],
]

/**
 * ほか依頼は紙面に対応する欄が無く、読み取り値も持たない(意味を持つのは is_reported だけ)。
 * 紙を突き合わせる流れを止めないよう、**処方内容の下・ページの末尾**に置く。
 * 分割 3 件のほかに医薬品漏れが並ぶ(実体は med_result 側なので ItemsPane が足す)。
 */
export const REQUEST_GROUP_AT_BOTTOM: FieldGroup = REQUEST_GROUP

/** 紙面の読み順に平たくしたもの(段の順 → 段の中は左から → 末尾にほか依頼)。 */
export const BASIC_RESULT_GROUPS: FieldGroup[] = [
  ...BASIC_RESULT_ROWS.flat(),
  REQUEST_GROUP,
]

/**
 * 値の読み取りではなく「分割して欲しい」という依頼。`selected_option` に読み取り値は入らず、
 * 意味を持つのは `is_reported` だけなので**値を出さずトグルだけ**を出す。
 * legacy も同じ3つを checkbox として扱っている(const.ts CHECKBOX_ITEM_KEYS)。
 */
export const REQUEST_FIELD_KEYS: BasicResultFieldKey[] = [
  '複数医療機関分割',
  '複数診療科分割',
  '複数患者分割',
]

/**
 * ラベルは紙面の語から**冗長な接頭辞だけ**を落とす(グループの見出しが文脈を持つ)。
 * 被保険者証記号/番号/枝番 → 記号/番号/枝番、依頼は「〜を分割」。
 * `保険者番号` と公費 2 つは縮めない — 顧客自身が使う語で、記号/番号と並ぶと指す先が曖昧になる。
 */
export const FIELD_LABELS: Partial<Record<BasicResultFieldKey, string>> = {
  id_11_patient_name_kanji: '患者名',
  id_11_patient_name_kana: 'カナ',
  id_12_patient_sex: '性別',
  id_13_patient_birthday: '生年月日',
  id_1_hospital_name: '医療機関名',
  id_4_hospital_department_name: '診療科',
  id_5_doctor_name_kanji: '医師名',
  id_5_doctor_name_kana: '医師カナ',
  id_51_prescription_date: '発行日',
  id_23_insurance_type: '保険種別',
  id_23_insurance_card_kigou: '記号',
  id_23_insurance_card_bangou: '番号',
  id_23_insurance_card_edaban: '枝番',
  id_22_insurance_patient_num: '保険者番号',
  id_27_1st_kouhi_futansha_num: '公費負担者番号',
  id_27_1st_kouhi_jukyuusha_num: '公費受給者番号',
  複数医療機関分割: '医療機関を分割',
  複数診療科分割: '診療科を分割',
  複数患者分割: '患者を分割',
}

const toValueLabelMap = (options: { label: string; value: string }[]) =>
  Object.fromEntries(options.map((o) => [o.value, o.label]))

// 性別/保険種別はJAHISコード(1/2)で格納される(converter.py _sex_code/_category_code)。
// 表示時はlegacyと同じ辞書(const.ts PRESCRIPTION_INFO)でラベル化し、辞書に無い値
// (旧データの生テキストや空)はそのまま表示する。
export const CODED_FIELD_VALUE_LABELS: Partial<
  Record<BasicResultFieldKey, Record<string, string>>
> = {
  id_12_patient_sex: toValueLabelMap(PRESCRIPTION_INFO.SEX_LIST),
  id_23_insurance_type: toValueLabelMap(PRESCRIPTION_INFO.INSURANCE_LIST),
}

export const formatBasicResultValue = (
  key: BasicResultFieldKey,
  value: string
): string => CODED_FIELD_VALUE_LABELS[key]?.[value] ?? value

/**
 * 値の修正(ADR-0032)。**ここに無いキーは表示と報告だけ**で、**在るキーは薬局を問わず直せる**。
 * 直せるかどうかを決めるのはこの 1 枚の辞書だけ。
 *
 * 医薬品名・用量を載せないのは、backend の `MedDBSearcher` が完全一致しない名前を
 * 閾値なしの Levenshtein 最近傍へ無警告で置換するため(打ち替えが別薬のコードになる)。
 *
 * **レセコン機種ごとの編集ゲートは latest では見ない**(松田 2026-09-03)。legacy / melphin が
 * `edit_mode.kana` / `.birthday` / `.insurance_num` で出し分けているのは継がない。
 * 機種由来で残すのは「空にできるか」(`validateFieldValue`)だけで、これは可否ではなく値の検査。
 */
export type FieldEditor = 'text' | 'choice' | 'date'

export const FIELD_EDITORS: Partial<Record<BasicResultFieldKey, FieldEditor>> =
  {
    id_27_1st_kouhi_futansha_num: 'text',
    id_27_1st_kouhi_jukyuusha_num: 'text',
    id_22_insurance_patient_num: 'text',
    id_23_insurance_card_kigou: 'text',
    id_23_insurance_card_bangou: 'text',
    id_23_insurance_card_edaban: 'text',
    id_23_insurance_type: 'choice',
    id_11_patient_name_kanji: 'text',
    id_11_patient_name_kana: 'text',
    id_5_doctor_name_kanji: 'text',
    id_5_doctor_name_kana: 'text',
    id_13_patient_birthday: 'date',
    id_12_patient_sex: 'choice',
  }

/** コード値(JAHIS 1/2)で持つ項目は自由入力にせず候補から選ばせる。 */
export const FIELD_CHOICES: Partial<Record<BasicResultFieldKey, CheckBox[]>> = {
  id_12_patient_sex: PRESCRIPTION_INFO.SEX_LIST,
  id_23_insurance_type: PRESCRIPTION_INFO.INSURANCE_LIST,
}

/**
 * backend は JAHIS CSV の生成に失敗すると 400 を返し、**編集そのものを保存しない**。
 * 落ちる形を送る前にここで止める。
 *
 * 生年月日: `convert_jp_to_jahis_ymd` が `[0-9]+年 / 月 / 日` を必須にしているので
 * 「令和元年」のような漢数字の年は通らない。元号は省略(西暦)も可。
 */
const BIRTHDAY_PATTERN =
  /^(令和|平成|昭和|大正|明治)?\d{1,4}年\d{1,2}月\d{1,2}日$/

export const BIRTHDAY_HINT =
  '和暦か西暦で「年月日」まで入れる(例: 昭和60年3月12日)'

export const validateFieldValue = (
  key: BasicResultFieldKey,
  value: string
): string | null => {
  const trimmed = value.trim()
  if (key === 'id_13_patient_birthday' && !BIRTHDAY_PATTERN.test(trimmed))
    return BIRTHDAY_HINT
  if (
    key === 'id_22_insurance_patient_num' &&
    trimmed === '' &&
    !localStorageWrapper.getItem('allowedInsuranceNumBlank')
  )
    return 'この機種では保険者番号を空にできない'
  return null
}
